import React, { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import {
  adminBrandsService,
  AdminBrand,
  BrandSearchParams,
  SearchBrandsResponse,
} from "../../services/admin/brands.admin.service";
import { DataTable, DataTablePagination } from "@/components/ui/data-table";
import type { ColumnDef } from "@tanstack/react-table";
import { AdminFilters } from "../../components/admin/AdminFilters";
import {
  Building2,
  RefreshCw,
  Mail,
  Calendar,
  DollarSign,
  Users,
  Globe,
  TrendingUp,
  Award,
  Activity,
  ExternalLink,
  MapPin,
  Tag,
  CheckCircle,
  XCircle,
  Clock,
  Pin,
  PinOff,
} from "lucide-react";
import { format } from "date-fns";
import { toast } from "react-hot-toast";
import { Switch } from "@headlessui/react";
import { formatstorageUrl } from "@/utils/formatters";
import { bulkInviteAdminService } from "../../services/admin/bulkInvite.admin.service";
import { brandRankingAdminService } from "../../services/admin/brandRanking.admin.service";
import { Checkbox } from "@/components/ui/checkbox";
import type { RowSelectionState } from "@tanstack/react-table";
import { Send } from "lucide-react";
import TikTokIcon from "@/assets/tiktok";
import { Dialog, DialogContent, DialogFooter, DialogHeader } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import type { TikTokPreReleaseBulkResult } from "../../services/admin/brands.admin.service";

// Custom debounce hook for search and simple filters
function useDebounce(callback: () => void, delay: number) {
  const debounceRef = React.useRef<NodeJS.Timeout>();

  return React.useCallback(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    debounceRef.current = setTimeout(() => callback(), delay);
  }, [callback, delay]);
}

// Skeleton Components
function StatCardSkeleton() {
  return (
    <div className="bg-transparent border border-border rounded-xl animate-pulse p-6">
      <div className="flex items-center justify-between space-x-4">
        <div className="space-y-2 flex-1">
          <div className="h-3 bg-muted rounded w-1/2"></div>
          <div className="h-8 bg-muted rounded w-1/3"></div>
        </div>
        <div className="h-12 w-12 rounded-2xl border border-border bg-transparent"></div>
      </div>
    </div>
  );
}

// Stats Card Component
interface StatCardProps {
  title: string;
  value: number | string;
  icon: React.ReactNode;
  color: string;
  formatter?: (value: number) => string;
}

function StatCard({ title, value, icon, color, formatter }: StatCardProps) {
  const displayValue =
    typeof value === "number" && formatter ? formatter(value) : value;
  const isWarm = color === "bg-orange-100 dark:bg-orange-500/15";

  return (
    <div className="bg-transparent border border-border rounded-xl transition-all duration-300 hover:border-[#d7785b]/40">
      <div className="p-6">
        <div className="flex items-center justify-between space-x-4">
          <div className="space-y-2 flex-1">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
              {title}
            </p>
            <p className="text-3xl font-bold text-foreground">{displayValue}</p>
          </div>
          <div
            className={`h-12 w-12 rounded-2xl flex items-center justify-center border ${
              isWarm
                ? "border-[#d7785b]/20 bg-transparent"
                : "border-border bg-transparent"
            }`}
          >
            <div className={isWarm ? "text-[#d7785b]" : "text-muted-foreground"}>
              {icon}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function AdminBrands() {
  const [brands, setBrands] = useState<AdminBrand[]>([]);
  const [loading, setLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(true);

  // Filter states
  // The admin dashboard links here with ?search=<brand> to open one brand.
  const [searchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState(() => searchParams.get("search") ?? "");
  const [sortBy, setSortBy] = useState<BrandSearchParams["sortBy"]>();
  const [sortOrder, setSortOrder] = useState<BrandSearchParams["sortOrder"]>("DESC");
  const [salesRange, setSalesRange] = useState<[number, number]>([0, 50000]);
  const [joinedAfter, setJoinedAfter] = useState("");
  const [sessionStatus, setSessionStatus] = useState<string>("");
  // PRE_RELEASE_TIKTOK: "" all, "PILOT" in the pilot, "NOT" outside it.
  const [tiktokPilot, setTiktokPilot] = useState<string>("");
  const [selectedBrand, setSelectedBrand] = useState<AdminBrand | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [confirmModal, setConfirmModal] = useState(false);

  // Ranking pins: checkbox selection for bulk pin/unpin, plus the per-brand
  // "set rank" input in the detail modal.
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [pinBusy, setPinBusy] = useState(false);
  const [rankDraft, setRankDraft] = useState("");
  const [rankSaving, setRankSaving] = useState(false);

  // Bulk invite settings (per-brand caps shown in the brand detail modal)
  const [bulkInviteSettingsLoading, setBulkInviteSettingsLoading] = useState(false);
  const [bulkInviteSettingsSaving, setBulkInviteSettingsSaving] = useState(false);
  const [bulkImportEnabledDraft, setBulkImportEnabledDraft] = useState(true);
  const [maxRecipientsDraft, setMaxRecipientsDraft] = useState<string>("");
  const [maxFileSizeDraft, setMaxFileSizeDraft] = useState<string>("");

  // Pagination
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  // Stats
  const [stats, setStats] = useState({
    totalBrands: 0,
    activeBrands: 0,
    totalSales: 0,
    averageSalesPerBrand: 0,
  });

  // Search brands
  const searchBrands = useCallback(async () => {
    setLoading(true);
    try {
      const params: BrandSearchParams = {
        page,
        limit: pageSize,
      };

      if (searchQuery) {
        params.keyword = searchQuery;
        params.searchBy = "COMPANY";
      }

      if (salesRange[0] > 0 || salesRange[1] < 50000) {
        params.minSales = salesRange[0];
        params.maxSales = salesRange[1];
      }

      if (joinedAfter) {
        params.joinedAfter = joinedAfter;
      }

      if (sessionStatus) {
        params.status = sessionStatus === "INSTALLED" ? "ACTIVE" : "INACTIVE";
      }

      if (tiktokPilot) {
        params.tiktokPreRelease = tiktokPilot === "PILOT";
      }

      if (sortBy) {
        params.sortBy = sortBy;
      }

      if (sortOrder) {
        params.sortOrder = sortOrder;
      }

      const response: SearchBrandsResponse =
        await adminBrandsService.searchBrands(params);
      setBrands(response.data);
      setTotal(response.pagination.total);
      setTotalPages(response.pagination.totalPages);
    } catch (error) {
      console.error("Failed to search brands:", error);
      setBrands([]);
      setTotal(0);
      setTotalPages(0);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, searchQuery, salesRange, joinedAfter, sessionStatus, sortBy, sortOrder, tiktokPilot]);

  // Debounced search for simple filters (search input, dropdown filters)
  const debouncedSimpleSearch = useDebounce(() => {
    setPage(1);
    searchBrands();
  }, 1000);

  // Filter handlers
  const handleSearch = (value: string) => {
    setSearchQuery(value);
    // Search input - auto-trigger with debounce
    if (value.trim()) {
      debouncedSimpleSearch();
    }
  };

  const handleSalesRangeChange = (newRange: [number, number]) => {
    setSalesRange(newRange);
    // Advanced filter - only update state, don't auto-trigger
  };

  const handleJoinedAfterChange = (date: string) => {
    setJoinedAfter(date);
    // Advanced filter - only update state, don't auto-trigger
  };

  const handleSessionStatusChange = (value: string | string[]) => {
    const newSessionStatus = Array.isArray(value) ? value[0] : value;
    setSessionStatus(newSessionStatus);
    // Advanced filter - only update state, don't auto-trigger
  };

  const handleSortByChange = (value: string | string[]) => {
    const newSortBy = Array.isArray(value) ? value[0] : value;
    setSortBy(newSortBy as BrandSearchParams["sortBy"]);
    // Simple filter - auto-trigger with debounce
    debouncedSimpleSearch();
  };

  // PRE_RELEASE_TIKTOK
  const handleTiktokPilotChange = (value: string | string[]) => {
    setTiktokPilot(Array.isArray(value) ? value[0] : value);
    debouncedSimpleSearch();
  };

  const handleSortOrderChange = (value: string | string[]) => {
    const newSortOrder = Array.isArray(value) ? value[0] : value;
    setSortOrder(newSortOrder as BrandSearchParams["sortOrder"]);
    // Simple filter - auto-trigger with debounce
    debouncedSimpleSearch();
  };

  const handleResetFilters = () => {
    setSearchQuery("");
    setSalesRange([0, 50000]);
    setJoinedAfter("");
    setSessionStatus("");
    setTiktokPilot("");
    setSortBy(undefined);
    setSortOrder("DESC");
    setPage(1);
    searchBrands();
  };

  const handleApplyFilters = () => {
    setPage(1);
    searchBrands();
  };

  // Fetch stats
  const fetchStats = async () => {
    setStatsLoading(true);
    try {
      const statsData = await adminBrandsService.getBrandStats();
      setStats(statsData);
    } catch (error) {
      console.error("Failed to fetch brand stats:", error);
    } finally {
      setStatsLoading(false);
    }
  };

  // Handle pagination change
  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  // Effect to fetch data when page changes
  useEffect(() => {
    searchBrands();
  }, [page, searchBrands]);

  // Initial load
  useEffect(() => {
    fetchStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle view brand details
  const handleViewBrand = async (brand: AdminBrand) => {
    setSelectedBrand(brand);
    setShowModal(true);
    setModalLoading(true);

    try {
      // Fetch detailed brand information if needed
      // const detailedData = await adminBrandsService.getBrandById(brand.id);
      // For now, we'll use the existing data since it's already comprehensive
      setModalLoading(false);
    } catch (error) {
      console.error("Failed to fetch detailed brand data:", error);
      setModalLoading(false);
    }

    void loadBulkInviteSettings(brand.sessionId);
    setRankDraft(brand.pinnedRank ? String(brand.pinnedRank) : "");
  };

  const selectedIds = Object.keys(rowSelection).filter((id) => rowSelection[id]);

  const applyPinLocally = (ids: string[], pinned: boolean) => {
    setBrands((current) =>
      current.map((b) =>
        ids.includes(b.id)
          ? { ...b, rankPinned: pinned, pinnedRank: pinned ? b.pinnedRank ?? null : null }
          : b
      )
    );
    setSelectedBrand((current) =>
      current && ids.includes(current.id)
        ? { ...current, rankPinned: pinned, pinnedRank: pinned ? current.pinnedRank ?? null : null }
        : current
    );
  };

  // Bulk pin freezes each brand at its own current rank; unpin lets it move
  // on the next scheduled run.
  const handleBulkPin = async (pinned: boolean) => {
    if (selectedIds.length === 0) return;
    setPinBusy(true);
    try {
      const result = await brandRankingAdminService.pinBrands(selectedIds, pinned);
      toast.success(`${result.updated} brand(s) ${pinned ? "pinned" : "unpinned"}`);
      applyPinLocally(selectedIds, pinned);
      setRowSelection({});
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to update pins");
    } finally {
      setPinBusy(false);
    }
  };

  const handleTogglePin = async (brand: AdminBrand, pinned: boolean) => {
    setPinBusy(true);
    try {
      await brandRankingAdminService.pinBrands([brand.id], pinned);
      toast.success(`${brand.companyName} ${pinned ? "pinned" : "unpinned"}`);
      applyPinLocally([brand.id], pinned);
      if (!pinned) setRankDraft("");
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to update pin");
    } finally {
      setPinBusy(false);
    }
  };

  // Pins to an explicit position and re-places every brand right away on
  // cached metrics, so the marketplace reflects it without waiting for a run.
  const handleSetRank = async () => {
    if (!selectedBrand) return;
    const rank = parseInt(rankDraft, 10);
    if (!Number.isFinite(rank) || rank < 1) {
      toast.error("Enter a rank of 1 or higher");
      return;
    }
    setRankSaving(true);
    try {
      const run = await brandRankingAdminService.setBrandRank(selectedBrand.id, rank);
      const placedAt = run.changes?.find((c) => c.id === selectedBrand.id)?.to ?? rank;
      toast.success(
        placedAt === rank
          ? `${selectedBrand.companyName} pinned to #${rank}`
          : `${selectedBrand.companyName} pinned to #${rank}, placed at #${placedAt} (slot taken)`
      );
      setSelectedBrand({
        ...selectedBrand,
        rankPinned: true,
        pinnedRank: rank,
        stats: { ...selectedBrand.stats, overallRank: placedAt },
      });
      await searchBrands();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to set rank");
    } finally {
      setRankSaving(false);
    }
  };

  const loadBulkInviteSettings = async (sessionId: string) => {
    setBulkInviteSettingsLoading(true);
    try {
      const settings = await bulkInviteAdminService.getSettings(sessionId);
      setBulkImportEnabledDraft(settings.bulkImportEnabled);
      setMaxRecipientsDraft(String(settings.maxRecipientsPerImport));
      setMaxFileSizeDraft(String(settings.maxFileSizeMb));
    } catch (error) {
      console.error("Failed to load bulk invite settings:", error);
      toast.error("Failed to load bulk invite settings");
    } finally {
      setBulkInviteSettingsLoading(false);
    }
  };

  const saveBulkInviteSettings = async () => {
    if (!selectedBrand) return;
    setBulkInviteSettingsSaving(true);
    try {
      const maxRecipientsPerImport =
        maxRecipientsDraft.trim() === "" ? null : Number(maxRecipientsDraft);
      const maxFileSizeMb =
        maxFileSizeDraft.trim() === "" ? null : Number(maxFileSizeDraft);

      await bulkInviteAdminService.updateSettings(selectedBrand.sessionId, {
        bulkImportEnabled: bulkImportEnabledDraft,
        maxRecipientsPerImport,
        maxFileSizeMb,
      });
      toast.success("Bulk invite settings saved");
    } catch (error) {
      console.error("Failed to save bulk invite settings:", error);
      toast.error(
        error instanceof Error ? error.message : "Failed to save bulk invite settings"
      );
    } finally {
      setBulkInviteSettingsSaving(false);
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedBrand(null);
  };

  // Format currency
  const formatCurrency = (value: number) =>
    `$${value.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;

  // Get status badge color
  const getStatusBadgeColor = (status: string) => {
    switch (status) {
      case "COMPLETED":
        return "bg-green-100 dark:bg-green-500/15 text-green-800 dark:text-green-300";
      case "IN_PROGRESS":
        return "bg-yellow-100 dark:bg-yellow-500/15 text-yellow-800 dark:text-yellow-300";
      case "PENDING":
        return "bg-red-100 dark:bg-red-500/15 text-red-800 dark:text-red-300";
      default:
        return "bg-muted text-foreground";
    }
  };
  const [toggleTestAccountLoading, setToggleTestAccountLoading] = useState(false);
  const toggleTestAccount = async (brandId: string, isTestAccount: boolean) => {
    try {
      setToggleTestAccountLoading(true);
      console.log(brandId, isTestAccount);
      const response = await adminBrandsService.toggleTestAccount(
        brandId,
        isTestAccount
      );
      if (response.status.toLowerCase() === "ok") {
        // Update the brands list with the new test account status
        setBrands(
          brands.map((brand) =>
            brand.id === brandId ? { ...brand, isTestAccount } : brand
          )
        );
        toast.success("Test account toggled successfully");
      } else {
        toast.error("Failed to toggle test account");
      }
    } catch (error) {
      console.error("Failed to toggle test account:", error);
      toast.error("Failed to toggle test account");
    } finally {
      setToggleTestAccountLoading(false);
      setConfirmModal(false);
    }
  };

  // PRE_RELEASE_TIKTOK: per-brand switch + paste-emails bulk enable.
  const [tiktokBusyId, setTiktokBusyId] = useState<string | null>(null);
  const toggleTikTokPreRelease = async (brand: AdminBrand) => {
    const enabled = !brand.tiktokPreRelease;
    setTiktokBusyId(brand.id);
    try {
      const result = await adminBrandsService.setTikTokPreRelease(brand.id, enabled);
      setBrands((current) =>
        current.map((b) =>
          b.id === brand.id
            ? { ...b, tiktokPreRelease: result.enabled, tiktokPreReleaseAt: result.enabledAt }
            : b
        )
      );
      toast.success(
        enabled
          ? `${brand.companyName} is in the TikTok pilot`
          : `${brand.companyName} is out of the TikTok pilot`
      );
    } catch {
      toast.error("Could not update the TikTok pilot");
    } finally {
      setTiktokBusyId(null);
    }
  };
  const [tiktokBulkOpen, setTiktokBulkOpen] = useState(false);
  const [tiktokBulkText, setTiktokBulkText] = useState("");
  const [tiktokBulkBusy, setTiktokBulkBusy] = useState(false);
  const [tiktokBulkResult, setTiktokBulkResult] = useState<TikTokPreReleaseBulkResult | null>(null);
  const submitTikTokBulk = async () => {
    const emails = Array.from(
      new Set(
        tiktokBulkText
          .split(/[\s,;]+/)
          .map((e) => e.trim().toLowerCase())
          .filter(Boolean)
      )
    );
    if (emails.length === 0) return;
    setTiktokBulkBusy(true);
    try {
      const result = await adminBrandsService.bulkEnableTikTokPreRelease(emails);
      setTiktokBulkResult(result);
      toast.success(
        `${result.enabled.length} enabled, ${result.alreadyEnabled.length} already in, ${result.unmatched.length} unmatched`
      );
      await searchBrands();
    } catch {
      toast.error("Could not enable the TikTok pilot for that list");
    } finally {
      setTiktokBulkBusy(false);
    }
  };
  const closeTikTokBulk = () => {
    setTiktokBulkOpen(false);
    setTiktokBulkText("");
    setTiktokBulkResult(null);
  };

  const [partnerConfirmModal, setPartnerConfirmModal] = useState(false);
  const [togglePartnerLoading, setTogglePartnerLoading] = useState(false);
  const togglePartnerAccount = async (brandId: string, isPartner: boolean) => {
    try {
      setTogglePartnerLoading(true);
      const response = await adminBrandsService.togglePartnerAccount(
        brandId,
        isPartner
      );
      if (response.status.toLowerCase() === "ok") {
        // Update the brands list with the new partner status
        setBrands(
          brands.map((brand) =>
            brand.id === brandId ? { ...brand, isPartner } : brand
          )
        );
        toast.success("Partner account toggled successfully");
      } else {
        toast.error("Failed to toggle partner account");
      }
    } catch (error) {
      console.error("Failed to toggle partner account:", error);
      toast.error("Failed to toggle partner account");
    } finally {
      setTogglePartnerLoading(false);
      setPartnerConfirmModal(false);
    }
  };

  // Table columns
  const columns: ColumnDef<AdminBrand>[] = [
    {
      id: "select",
      header: ({ table }) => (
        <div onClick={(e) => e.stopPropagation()}>
          <Checkbox
            checked={
              table.getIsAllPageRowsSelected() ||
              (table.getIsSomePageRowsSelected() && "indeterminate")
            }
            onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
            aria-label="Select all brands on this page"
          />
        </div>
      ),
      meta: { width: "w-10" },
      cell: ({ row }) => (
        <div onClick={(e) => e.stopPropagation()}>
          <Checkbox
            checked={row.getIsSelected()}
            onCheckedChange={(value) => row.toggleSelected(!!value)}
            aria-label={`Select ${row.original.companyName}`}
          />
        </div>
      ),
    },
    {
      id: "rank",
      header: "Rank",
      meta: { width: "w-24" },
      cell: ({ row }) => {
        const brand = row.original;
        const rank = brand.stats.overallRank;
        return (
          <div className="flex items-center gap-1.5 text-sm">
            <span className={`font-medium ${rank ? "text-foreground" : "text-muted-foreground"}`}>
              {rank ? `#${rank}` : "—"}
            </span>
            {brand.rankPinned && (
              <Pin
                className="w-3 h-3 text-[#d7785b] shrink-0"
                aria-label={brand.pinnedRank ? `Pinned to #${brand.pinnedRank}` : "Pinned"}
              />
            )}
          </div>
        );
      },
    },
    {
      id: "brand",
      header: "Brand",
      meta: { width: "w-52" },
      cell: ({ row }) => {
        const brand = row.original;
        return (
          <div className="flex items-center gap-3 min-w-0">
            {brand.brandProfileImage ? (
              <img
                src={formatstorageUrl(brand.brandProfileImage)}
                alt=""
                className="w-8 h-8 rounded-lg object-cover shrink-0"
              />
            ) : (
              <div className="w-8 h-8 rounded-lg bg-[#d7785b]/10 flex items-center justify-center shrink-0">
                <Building2 className="w-4 h-4 text-[#d7785b]" />
              </div>
            )}
            <div className="font-medium text-foreground truncate">
              {brand.companyName}
            </div>
          </div>
        );
      },
    },
    {
      id: "owner",
      header: "Owner",
      meta: { width: "w-36" },
      cell: ({ row }) => (
        <div className="text-sm text-foreground truncate">
          {row.original.firstName} {row.original.lastName}
        </div>
      ),
    },
    {
      id: "email",
      header: "Email",
      meta: { width: "w-52" },
      cell: ({ row }) => (
        <div className="flex items-center gap-1 text-sm text-foreground min-w-0">
          <Mail className="w-3 h-3 text-muted-foreground shrink-0" />
          <span className="truncate">{row.original.email}</span>
        </div>
      ),
    },
    {
      id: "website",
      header: "Website",
      meta: { width: "w-40" },
      cell: ({ row }) => {
        const brand = row.original;
        return brand.website ? (
          <div className="flex items-center gap-1 text-sm text-muted-foreground min-w-0">
            <Globe className="w-3 h-3 text-muted-foreground shrink-0" />
            <a
              href={brand.website}
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-[#d7785b] truncate max-w-32"
            >
              {brand.website.replace(/^https?:\/\//, "")}
            </a>
          </div>
        ) : (
          <span className="text-sm text-muted-foreground">—</span>
        );
      },
    },
    {
      id: "location",
      header: "Location",
      meta: { width: "w-32" },
      cell: ({ row }) => (
        <div className="flex items-center gap-1 text-sm text-muted-foreground">
          <MapPin className="w-3 h-3 text-muted-foreground shrink-0" />
          <span className="truncate">{row.original.country}</span>
        </div>
      ),
    },
    {
      id: "joinedAt",
      header: "Joined",
      meta: { fit: true },
      cell: ({ row }) => (
        <div className="flex items-center gap-1 text-sm text-muted-foreground">
          <Calendar className="w-3 h-3 text-muted-foreground shrink-0" />
          {format(new Date(row.original.joinedAt), "MMM dd, yyyy")}
        </div>
      ),
    },
    {
      id: "sales",
      header: "Sales",
      meta: { width: "w-28" },
      cell: ({ row }) => (
        <div className="flex items-center gap-1">
          <DollarSign className="w-3 h-3 text-muted-foreground" />
          <span
            className={`font-medium ${
              row.original.stats.totalSales > 0
                ? "text-green-600 dark:text-green-400"
                : "text-muted-foreground"
            }`}
          >
            {formatCurrency(row.original.stats.totalSales)}
          </span>
        </div>
      ),
    },
    {
      id: "affiliates",
      header: "Affiliates",
      meta: { width: "w-24" },
      cell: ({ row }) => (
        <div className="flex items-center gap-1">
          <Users className="w-3 h-3 text-muted-foreground" />
          <span className="font-medium text-foreground">
            {row.original.stats.totalAffiliates}
          </span>
        </div>
      ),
    },
    {
      id: "accountType",
      header: "Test Account",
      meta: { width: "w-28" },
      cell: ({ row }) => {
        const brand = row.original;
        return (
          <div className="flex items-center gap-1">
            <Switch
              checked={brand.isTestAccount}
              onChange={() => {
                setConfirmModal(true);
                setSelectedBrand(brand);
              }}
              disabled={toggleTestAccountLoading}
              className={` relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                brand.isTestAccount ? "bg-[#d7785b]" : "bg-muted"
              }`}
            >
              <span
                className={`inline-block size-4 transform rounded-full bg-card transition-transform ${
                  brand.isTestAccount ? "translate-x-6" : "translate-x-1"
                }`}
              />
            </Switch>
          </div>
        );
      },
    },
    {
      // PRE_RELEASE_TIKTOK
      id: "tiktokPreRelease",
      header: "TikTok pilot",
      meta: { width: "w-28" },
      cell: ({ row }) => {
        const brand = row.original;
        const on = !!brand.tiktokPreRelease;
        return (
          <div
            className="flex items-center gap-1"
            title={
              on && brand.tiktokPreReleaseAt
                ? `In the pilot since ${format(new Date(brand.tiktokPreReleaseAt), "MMM dd, yyyy")}`
                : "Let this brand see TikTok Shop analytics (beta) and the coming-soon creator tools"
            }
          >
            <Switch
              checked={on}
              onChange={() => toggleTikTokPreRelease(brand)}
              disabled={tiktokBusyId === brand.id}
              className={` relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                on ? "bg-[#d7785b]" : "bg-muted"
              }`}
            >
              <span
                className={`inline-block size-4 transform rounded-full bg-card transition-transform ${
                  on ? "translate-x-6" : "translate-x-1"
                }`}
              />
            </Switch>
          </div>
        );
      },
    },
    {
      id: "partner",
      header: "Partner",
      meta: { width: "w-24" },
      cell: ({ row }) => {
        const brand = row.original;
        return (
          <div className="flex items-center gap-1">
            <Switch
              checked={!!brand.isPartner}
              onChange={() => {
                setPartnerConfirmModal(true);
                setSelectedBrand(brand);
              }}
              disabled={togglePartnerLoading}
              className={` relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                brand.isPartner ? "bg-[#d7785b]" : "bg-muted"
              }`}
            >
              <span
                className={`inline-block size-4 transform rounded-full bg-card transition-transform ${
                  brand.isPartner ? "translate-x-6" : "translate-x-1"
                }`}
              />
            </Switch>
          </div>
        );
      },
    },
    {
      id: "plan",
      header: "Plan",
      meta: { width: "w-28" },
      cell: ({ row }) => (
        <div className="flex items-center gap-1 text-sm">
          {row.original.plan ? (
            <span className="font-medium text-foreground">
              {row.original.plan.name}
            </span>
          ) : (
            <span className="text-muted-foreground">No plan</span>
          )}
        </div>
      ),
    },
    {
      id: "sessionStatus",
      header: "Installation",
      meta: { width: "w-32" },
      cell: ({ row }) => (
        <div className="flex items-center gap-1">
          {row.original.isActive ? (
            <div className="flex items-center gap-1.5">
              <CheckCircle className="w-4 h-4 text-green-600 dark:text-green-400" />
              <span className="text-sm font-medium text-green-700 dark:text-green-300">
                Installed
              </span>
            </div>
          ) : (
            <div className="flex items-center gap-1.5">
              <XCircle className="w-4 h-4 text-red-600 dark:text-red-400" />
              <span className="text-sm font-medium text-red-700 dark:text-red-300">
                Uninstalled
              </span>
            </div>
          )}
        </div>
      ),
    },
    {
      id: "status",
      header: "Status",
      meta: { width: "w-52" },
      cell: ({ row }) => {
        const brand = row.original;
        return (
          <div className="flex items-center gap-1.5 whitespace-nowrap">
            <div
              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadgeColor(
                brand.onboardingStatus
              )}`}
            >
              {brand.onboardingStatus === "COMPLETED" && (
                <CheckCircle className="w-3 h-3 mr-1" />
              )}
              {brand.onboardingStatus === "IN_PROGRESS" && (
                <Clock className="w-3 h-3 mr-1" />
              )}
              {brand.onboardingStatus === "PENDING" && (
                <XCircle className="w-3 h-3 mr-1" />
              )}
              {brand.onboardingStatus.replace("_", " ")}
            </div>
            <div
              className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium ${
                brand.isActive
                  ? "bg-green-100 dark:bg-green-500/15 text-green-800 dark:text-green-300"
                  : "bg-muted text-muted-foreground"
              }`}
            >
              {brand.isActive ? "Active" : "Inactive"}
            </div>
          </div>
        );
      },
    },
  ];

  return (
    <div className="space-y-8 max-w-7xl mx-auto">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-4xl font-semibold text-foreground mb-2">
            Brand Management
          </h1>
          <p className="text-muted-foreground">
            Manage and monitor all brands on your platform.
          </p>
        </div>
        <button
          onClick={() => {
            searchBrands();
            fetchStats();
          }}
          className="flex items-center gap-2 px-6 py-3 text-white rounded-xl transition-all duration-200 shadow-sm hover:shadow-md"
          style={{ backgroundColor: "#d7785b" }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = "#c66a50";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = "#d7785b";
          }}
        >
          <RefreshCw className="w-4 h-4" />
          Refresh
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5 mb-8">
        {statsLoading ? (
          Array.from({ length: 4 }).map((_, index) => (
            <StatCardSkeleton key={index} />
          ))
        ) : (
          <>
            <StatCard
              title="Total Brands"
              value={stats.totalBrands}
              icon={<Building2 className="w-5 h-5 text-foreground" />}
              color="bg-muted"
            />
            <StatCard
              title="Active Brands"
              value={stats.activeBrands}
              icon={
                <Activity className="w-5 h-5" style={{ color: "#d7785b" }} />
              }
              color="bg-orange-100 dark:bg-orange-500/15"
            />
            <StatCard
              title="Total Sales"
              value={stats.totalSales}
              icon={<TrendingUp className="w-5 h-5 text-foreground" />}
              color="bg-muted"
              formatter={formatCurrency}
            />
            <StatCard
              title="Avg. Sales"
              value={stats.averageSalesPerBrand}
              icon={<Award className="w-5 h-5" style={{ color: "#d7785b" }} />}
              color="bg-orange-100 dark:bg-orange-500/15"
              formatter={formatCurrency}
            />
          </>
        )}
      </div>

      {/* PRE_RELEASE_TIKTOK: paste the outreach list, matched brands join the pilot */}
      <Dialog open={tiktokBulkOpen} onOpenChange={(open) => (open ? setTiktokBulkOpen(true) : closeTikTokBulk())}>
        <DialogContent>
          <DialogHeader showCloseButton onClose={closeTikTokBulk}>
            Add brands to the TikTok pilot
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Paste brand login emails, one per line or comma-separated. Matched brands start seeing
            TikTok Shop analytics (beta) and the coming-soon creator tools; unmatched emails are listed
            back so you can fix them.
          </p>
          <Textarea
            className="mt-3 min-h-40 font-mono text-xs"
            placeholder={"owner@brand-one.com\nhello@brand-two.com"}
            value={tiktokBulkText}
            onChange={(e) => setTiktokBulkText(e.target.value)}
            disabled={tiktokBulkBusy}
          />
          {tiktokBulkResult && (
            <div className="mt-3 space-y-1 text-sm">
              <p className="text-foreground">
                Enabled {tiktokBulkResult.enabled.length}, already in {tiktokBulkResult.alreadyEnabled.length}.
              </p>
              {tiktokBulkResult.unmatched.length > 0 && (
                <p className="text-muted-foreground">
                  Not matched: {tiktokBulkResult.unmatched.join(", ")}
                </p>
              )}
            </div>
          )}
          <DialogFooter>
            <button
              type="button"
              className="px-3 py-1.5 rounded-md border border-border text-sm text-foreground hover:bg-muted"
              onClick={closeTikTokBulk}
              disabled={tiktokBulkBusy}
            >
              Close
            </button>
            <button
              type="button"
              className="px-3 py-1.5 rounded-md bg-[#d7785b] text-white text-sm hover:bg-[#c66a4f] disabled:opacity-50"
              onClick={submitTikTokBulk}
              disabled={tiktokBulkBusy || !tiktokBulkText.trim()}
            >
              {tiktokBulkBusy ? "Enabling…" : "Enable for these brands"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Enhanced Filters */}
      <div className="">
        <AdminFilters
          filters={[
            {
              label: "Sort By",
              value: sortBy || "",
              onChange: handleSortByChange,
              showLabel: true,
              options: [
                { label: "Join Date", value: "JOIN_DATE" },
                { label: "Company", value: "NAME" },
                { label: "Sales", value: "REVENUE" },
                { label: "Commissions", value: "COMMISSION" },
                { label: "Affiliates", value: "AFFILIATES" },
              ],
            },
            {
              label: "Sort Order",
              value: sortOrder || "",
              onChange: handleSortOrderChange,
              showLabel: true,
              options: [
                { label: "Descending", value: "DESC" },
                { label: "Ascending", value: "ASC" },
              ],
            },
            {
              label: "Installation Status",
              value: sessionStatus || "",
              onChange: handleSessionStatusChange,
              showLabel: true,
              options: [
                { label: "All", value: "" },
                { label: "Installed", value: "INSTALLED" },
                { label: "Uninstalled", value: "UNINSTALLED" },
              ],
            },
            {
              // PRE_RELEASE_TIKTOK
              label: "TikTok pilot",
              value: tiktokPilot,
              onChange: handleTiktokPilotChange,
              showLabel: true,
              options: [
                { label: "All", value: "" },
                { label: "In the pilot", value: "PILOT" },
                { label: "Not in the pilot", value: "NOT" },
              ],
            },
          ]}
          searchPlaceholder="Search brands by company name..."
          onSearch={handleSearch}
          searchValue={searchQuery}
          revenueRange={{
            min: 0,
            max: 50000,
            value: salesRange,
            onChange: handleSalesRangeChange,
            label: "Sales",
          }}
          dateFilter={{
            value: joinedAfter,
            onChange: handleJoinedAfterChange,
            label: "Joined After",
            placeholder: "Select date",
          }}
          onReset={handleResetFilters}
          onApply={handleApplyFilters}
        />
      </div>

      {/* Brands Table */}
      <div className="bg-card rounded-xl border border-border shadow-sm">
        <div className="p-8 border-b border-border flex flex-wrap items-center justify-between gap-4">
          <h3 className="text-xl font-semibold text-foreground">
            Brands ({total.toLocaleString()})
          </h3>
          {/* PRE_RELEASE_TIKTOK */}
          <button
            type="button"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-border text-sm text-foreground hover:bg-muted"
            onClick={() => setTiktokBulkOpen(true)}
          >
            <TikTokIcon className="w-3.5 h-3.5" />
            TikTok pilot by email list
          </button>
          {selectedIds.length > 0 && (
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">
                {selectedIds.length} selected
              </span>
              <button
                type="button"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-border text-sm text-foreground hover:bg-muted disabled:opacity-50"
                disabled={pinBusy}
                onClick={() => handleBulkPin(false)}
              >
                <PinOff className="w-3.5 h-3.5" />
                Unpin
              </button>
              <button
                type="button"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-[#d7785b] text-white text-sm hover:bg-[#c66a4f] disabled:opacity-50"
                disabled={pinBusy}
                onClick={() => handleBulkPin(true)}
              >
                <Pin className="w-3.5 h-3.5" />
                Pin
              </button>
              <button
                type="button"
                className="text-sm text-muted-foreground hover:text-foreground px-2"
                disabled={pinBusy}
                onClick={() => setRowSelection({})}
              >
                Clear
              </button>
            </div>
          )}
        </div>

        <DataTable
          data={brands}
          columns={columns}
          isLoading={loading}
          emptyText={
            searchQuery
              ? "No brands found matching your search criteria"
              : "No brands have joined yet"
          }
          onRowClick={handleViewBrand}
          getRowId={(row) => row.id}
          rowSelection={rowSelection}
          onRowSelectionChange={setRowSelection}
        />

        {/* Pagination */}
        {totalPages > 1 && (
          <DataTablePagination
            page={page}
            totalPages={totalPages}
            total={total}
            pageSize={pageSize}
            onPageSizeChange={(size) => {
              setPageSize(size);
              setPage(1);
            }}
            pageSizeOptions={[10, 20, 50, 100]}
            onPageChange={handlePageChange}
            noun="brands"
          />
        )}
      </div>

        {
          confirmModal && selectedBrand && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-card rounded-lg p-6 max-w-6xl w-1/2 mx-4 max-h-[90vh] overflow-y-auto">
                <h2 className="text-xl font-bold text-foreground">{!selectedBrand.isTestAccount ? "Enable" : "Disable"} Test Account </h2>
                <p className="text-muted-foreground">Are you sure you want to {!selectedBrand.isTestAccount ? "enable" : "disable"} test account for {selectedBrand?.companyName}?</p>
                <div className="flex items-center justify-end gap-2">
                  <button className="bg-muted text-foreground px-4 py-2 rounded-md" onClick={() => setConfirmModal(false)}>Cancel</button>
                  <button className="bg-[#d7785b] text-white px-4 py-2 rounded-md" onClick={() => toggleTestAccount(selectedBrand.id, !selectedBrand.isTestAccount)}>{!selectedBrand.isTestAccount ? "Enable" : toggleTestAccountLoading ? "saving..." : "Disable"} </button>
                </div>
              </div>
            </div>
          )
        }

        {
          partnerConfirmModal && selectedBrand && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-card rounded-lg p-6 max-w-6xl w-1/2 mx-4 max-h-[90vh] overflow-y-auto">
                <h2 className="text-xl font-bold text-foreground">{!selectedBrand.isPartner ? "Enable" : "Disable"} Partner Account </h2>
                <p className="text-muted-foreground">Are you sure you want to {!selectedBrand.isPartner ? "enable" : "disable"} partner access for {selectedBrand?.companyName}? Partner stores can subscribe to the free Partner plan (all Scale features, no monthly fee, no revenue share).</p>
                <div className="flex items-center justify-end gap-2">
                  <button className="bg-muted text-foreground px-4 py-2 rounded-md" onClick={() => setPartnerConfirmModal(false)}>Cancel</button>
                  <button className="bg-[#d7785b] text-white px-4 py-2 rounded-md" onClick={() => togglePartnerAccount(selectedBrand.id, !selectedBrand.isPartner)}>{togglePartnerLoading ? "saving..." : !selectedBrand.isPartner ? "Enable" : "Disable"} </button>
                </div>
              </div>
            </div>
          )
        }

      {/* Enhanced Brand Detail Modal */}
      {showModal && selectedBrand && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-card rounded-lg p-6 max-w-6xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-foreground">Brand Details</h2>
              <button
                onClick={closeModal}
                className="text-muted-foreground hover:text-muted-foreground"
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            {modalLoading ? (
              <div className="flex items-center justify-center py-12">
                <div
                  className="animate-spin rounded-full h-12 w-12 border-b-2"
                  style={{ borderColor: "#d7785b" }}
                ></div>
              </div>
            ) : (
              <div className="space-y-6">
                {/* Header Section */}
                <div className="bg-gradient-to-r from-muted to-orange-100/60 dark:to-orange-500/10 rounded-xl p-6">
                  <div className="flex items-center gap-6">
                    {selectedBrand.brandProfileImage ? (
                      <img
                        src={selectedBrand.brandProfileImage}
                        alt=""
                        className="w-20 h-20 rounded-lg object-cover"
                      />
                    ) : (
                      <div className="w-20 h-20 rounded-lg bg-orange-100 dark:bg-orange-500/15 flex items-center justify-center">
                        <Building2
                          className="w-10 h-10"
                          style={{ color: "#d7785b" }}
                        />
                      </div>
                    )}
                    <div className="flex-1">
                      <h3 className="text-2xl font-bold text-foreground">
                        {selectedBrand.companyName}
                      </h3>
                      <p className="text-muted-foreground text-lg">
                        {selectedBrand.firstName} {selectedBrand.lastName}
                      </p>
                      <div className="flex items-center gap-4 mt-2">
                        <div className="flex items-center gap-1">
                          <Mail className="w-4 h-4 text-muted-foreground" />
                          <span className="text-sm text-muted-foreground">
                            {selectedBrand.email}
                          </span>
                        </div>
                        {selectedBrand.website && (
                          <div className="flex items-center gap-1">
                            <ExternalLink className="w-4 h-4 text-muted-foreground" />
                            <a
                              href={selectedBrand.website}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-sm hover:underline"
                              style={{ color: "#d7785b" }}
                            >
                              Website
                            </a>
                          </div>
                        )}
                        <div
                          className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs ${getStatusBadgeColor(
                            selectedBrand.onboardingStatus
                          )}`}
                        >
                          {selectedBrand.onboardingStatus === "COMPLETED" && (
                            <CheckCircle className="w-3 h-3" />
                          )}
                          {selectedBrand.onboardingStatus === "IN_PROGRESS" && (
                            <Clock className="w-3 h-3" />
                          )}
                          {selectedBrand.onboardingStatus === "PENDING" && (
                            <XCircle className="w-3 h-3" />
                          )}
                          {selectedBrand.onboardingStatus.replace("_", " ")}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Performance Metrics */}
                  <div className="bg-card border border-border rounded-xl p-6">
                    <h4 className="text-lg font-semibold text-foreground mb-4">
                      Performance Metrics
                    </h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="bg-green-50 dark:bg-green-500/10 p-4 rounded-lg text-center">
                        <div className="text-sm text-green-600 dark:text-green-400 font-medium">
                          Total Sales
                        </div>
                        <div className="text-xl font-bold text-green-900">
                          {formatCurrency(selectedBrand.stats.totalSales)}
                        </div>
                        <div className="text-xs text-green-600 dark:text-green-400 mt-1">
                          Rank #{selectedBrand.stats.revenueRank}
                        </div>
                      </div>
                      <div className="bg-blue-50 dark:bg-blue-500/10 p-4 rounded-lg text-center">
                        <div className="text-sm text-blue-600 dark:text-blue-400 font-medium">
                          Commissions
                        </div>
                        <div className="text-xl font-bold text-blue-900">
                          {formatCurrency(selectedBrand.stats.totalCommissions)}
                        </div>
                        <div className="text-xs text-blue-600 dark:text-blue-400 mt-1">
                          Rank #{selectedBrand.stats.commissionRank}
                        </div>
                      </div>
                      <div className="bg-orange-50 dark:bg-orange-500/10 p-4 rounded-lg text-center">
                        <div className="text-sm text-[#d7785b] font-medium">
                          Affiliates
                        </div>
                        <div className="text-xl font-bold text-orange-900 dark:text-orange-200">
                          {selectedBrand.stats.totalAffiliates}
                        </div>
                        <div className="text-xs text-[#d7785b] mt-1">
                          Rank #{selectedBrand.stats.affiliatesRank}
                        </div>
                      </div>
                      <div className="bg-yellow-50 dark:bg-yellow-500/10 p-4 rounded-lg text-center">
                        <div className="text-sm text-yellow-600 dark:text-yellow-400 font-medium">
                          Overall Rank
                        </div>
                        <div className="text-xl font-bold text-yellow-900">
                          #{selectedBrand.stats.overallRank}
                        </div>
                        <div className="text-xs text-yellow-600 dark:text-yellow-400 mt-1">
                          {selectedBrand.rankPinned
                            ? selectedBrand.pinnedRank
                              ? `Pinned to #${selectedBrand.pinnedRank}`
                              : "Pinned (holding)"
                            : "Free to move"}
                        </div>
                      </div>
                    </div>

                    {/* Ranking controls */}
                    <div className="mt-4 pt-4 border-t border-border flex flex-wrap items-center gap-3">
                      <button
                        type="button"
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-border text-sm text-foreground hover:bg-muted disabled:opacity-50"
                        disabled={pinBusy || rankSaving}
                        onClick={() =>
                          handleTogglePin(selectedBrand, !selectedBrand.rankPinned)
                        }
                      >
                        {selectedBrand.rankPinned ? (
                          <PinOff className="w-3.5 h-3.5" />
                        ) : (
                          <Pin className="w-3.5 h-3.5" />
                        )}
                        {pinBusy ? "Saving..." : selectedBrand.rankPinned ? "Unpin" : "Pin"}
                      </button>
                      <div className="flex items-center gap-2">
                        <input
                          type="number"
                          min={1}
                          value={rankDraft}
                          onChange={(e) => setRankDraft(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") void handleSetRank();
                          }}
                          placeholder="Rank"
                          className="w-24 px-3 py-1.5 rounded-md border border-border bg-background text-sm text-foreground"
                        />
                        <button
                          type="button"
                          className="px-3 py-1.5 rounded-md bg-[#d7785b] text-white text-sm hover:bg-[#c66a4f] disabled:opacity-50"
                          disabled={rankSaving || pinBusy || !rankDraft}
                          onClick={() => void handleSetRank()}
                        >
                          {rankSaving ? "Placing..." : "Set rank"}
                        </button>
                      </div>
                      <p className="w-full text-xs text-muted-foreground">
                        Set rank pins this brand to a position and re-places every brand
                        immediately on cached metrics. Unpinned brands move on the next
                        scheduled run.
                      </p>
                    </div>
                  </div>

                  {/* Program Details */}
                  <div className="bg-card border border-border rounded-xl p-6">
                    <h4 className="text-lg font-semibold text-foreground mb-4">
                      Affiliate Program
                    </h4>
                    {selectedBrand.program ? (
                      <div className="space-y-3">
                        <div className="flex items-center gap-2">
                          <Award className="w-4 h-4 text-muted-foreground" />
                          <span className="text-sm text-muted-foreground">
                            Commission Type:
                          </span>
                          <span className="text-sm font-medium">
                            {selectedBrand.program.commissionType}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <DollarSign className="w-4 h-4 text-muted-foreground" />
                          <span className="text-sm text-muted-foreground">
                            Commission Value:
                          </span>
                          <span className="text-sm font-medium">
                            {selectedBrand.program.commissionValue}
                            {selectedBrand.program.commissionType ===
                              "PERCENTAGE" && "%"}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Tag className="w-4 h-4 text-muted-foreground" />
                          <span className="text-sm text-muted-foreground">
                            Discount Value:
                          </span>
                          <span className="text-sm font-medium">
                            {selectedBrand.program.discountValue}%
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Activity className="w-4 h-4 text-muted-foreground" />
                          <span className="text-sm text-muted-foreground">Status:</span>
                          <span
                            className={`text-sm font-medium ${
                              selectedBrand.program.status === "ACTIVE"
                                ? "text-green-600 dark:text-green-400"
                                : "text-red-600 dark:text-red-400"
                            }`}
                          >
                            {selectedBrand.program.status}
                          </span>
                        </div>
                      </div>
                    ) : (
                      <div className="text-center py-8">
                        <div className="text-muted-foreground mb-2">
                          <Award className="w-12 h-12 mx-auto" />
                        </div>
                        <p className="text-muted-foreground text-sm">
                          No affiliate program configured yet
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Bulk Invite Settings */}
                <div className="bg-card border border-border rounded-xl p-6">
                  <div className="flex items-center gap-2 mb-4">
                    <Send className="w-5 h-5 text-muted-foreground" />
                    <h4 className="text-lg font-semibold text-foreground">
                      Bulk Affiliate Invite Settings
                    </h4>
                  </div>

                  {bulkInviteSettingsLoading ? (
                    <div className="text-sm text-muted-foreground py-4">Loading settings…</div>
                  ) : (
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="text-sm font-medium text-foreground">
                            Bulk import enabled
                          </div>
                          <div className="text-xs text-muted-foreground">
                            Allow this brand to bulk-invite affiliates via CSV/Excel
                            import.
                          </div>
                        </div>
                        <Switch
                          checked={bulkImportEnabledDraft}
                          onChange={setBulkImportEnabledDraft}
                          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                            bulkImportEnabledDraft ? "bg-[#d7785b]" : "bg-muted"
                          }`}
                        >
                          <span
                            className={`inline-block size-4 transform rounded-full bg-card transition-transform ${
                              bulkImportEnabledDraft ? "translate-x-6" : "translate-x-1"
                            }`}
                          />
                        </Switch>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-xs font-medium text-muted-foreground mb-1">
                            Max recipients per import
                          </label>
                          <input
                            type="number"
                            min={1}
                            placeholder="Platform default"
                            value={maxRecipientsDraft}
                            onChange={(e) => setMaxRecipientsDraft(e.target.value)}
                            className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#d7785b]/20"
                          />
                          <p className="text-[11px] text-muted-foreground mt-1">
                            Clear the field and save to reset to the platform default
                            (env).
                          </p>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-muted-foreground mb-1">
                            Max file size (MB)
                          </label>
                          <input
                            type="number"
                            min={1}
                            placeholder="Platform default"
                            value={maxFileSizeDraft}
                            onChange={(e) => setMaxFileSizeDraft(e.target.value)}
                            className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#d7785b]/20"
                          />
                          <p className="text-[11px] text-muted-foreground mt-1">
                            Clear the field and save to reset to the platform default
                            (env).
                          </p>
                        </div>
                      </div>

                      <div className="flex justify-end">
                        <button
                          onClick={saveBulkInviteSettings}
                          disabled={bulkInviteSettingsSaving}
                          className="px-4 py-2 text-sm text-white rounded-lg transition-colors disabled:opacity-50"
                          style={{ backgroundColor: "#d7785b" }}
                        >
                          {bulkInviteSettingsSaving ? "Saving…" : "Save settings"}
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                {/* Niches Section */}
                {selectedBrand.niches.length > 0 && (
                  <div className="bg-card border border-border rounded-xl p-6">
                    <h4 className="text-lg font-semibold text-foreground mb-4">
                      Target Niches
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {selectedBrand.niches.map((niche, index) => (
                        <div
                          key={index}
                          className="flex items-center gap-1 px-3 py-2 bg-orange-100 dark:bg-orange-500/15 text-orange-800 dark:text-orange-300 rounded-full text-sm"
                        >
                          <Tag className="w-3 h-3" />
                          {niche.name}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Social Media Section */}
                {selectedBrand.socialMedia.length > 0 && (
                  <div className="bg-card border border-border rounded-xl p-6">
                    <h4 className="text-lg font-semibold text-foreground mb-4">
                      Social Media Presence
                    </h4>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                      {selectedBrand.socialMedia.map((social, index) => (
                        <div
                          key={index}
                          className="flex items-center gap-2 p-3 bg-muted rounded-lg"
                        >
                          <div className="w-8 h-8 bg-orange-100 dark:bg-orange-500/15 rounded-full flex items-center justify-center">
                            <span className="text-xs font-medium text-[#d7785b]">
                              {social.platform.charAt(0)}
                            </span>
                          </div>
                          <div>
                            <div className="text-xs text-muted-foreground uppercase">
                              {social.platform}
                            </div>
                            <div className="text-sm font-medium text-foreground">
                              @{social.username}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Description Section */}
                {selectedBrand.description && (
                  <div className="bg-card border border-border rounded-xl p-6">
                    <h4 className="text-lg font-semibold text-foreground mb-4">
                      Description
                    </h4>
                    <div
                      className="prose prose-sm max-w-none text-muted-foreground"
                      dangerouslySetInnerHTML={{
                        __html: selectedBrand.description,
                      }}
                    />
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
